import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { of } from 'rxjs';
import { delay } from 'rxjs/operators';
import { ShoppingCartItemResponse } from '../../../../shared/services/responses/platform/get-shopping-cart.response';
import { MatFormField, MatLabel } from "@angular/material/form-field";
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { KitPaymentService } from '../../../../shared/services/apis/platform/kit-payment.service';
import { GetKitPaymentCouponResponse } from '../../../../shared/services/responses/platform/get-kit-payment-coupon.response';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';

@Component({
  selector: 'app-kit-payment-book',
  templateUrl: './kit-payment-book.component.html',
  styleUrls: ['./kit-payment-book.component.css'],
  imports: [MatFormField, MatLabel, FormsModule, ReactiveFormsModule, CommonModule, MatInputModule]
})
export class KitPaymentBookComponent implements OnInit {

  @Input() book!: ShoppingCartItemResponse;
  @Output() quantityChange = new EventEmitter<{ idKit: number, quantity: number }>();
  @Output() applyDiscount = new EventEmitter<{ idKit: number, discountCode: string, discountValue: number }>();

  authorDiscountCode: string = '';
  discountMessage: string = '';
  isAuthor: boolean = false;
  private authorMaxDiscountPerUnit: number = 0;

  constructor(private kitPaymentService: KitPaymentService,
    private alertService: AlertService
  ) { }

  ngOnInit(): void {
    this.checkAuthorDiscount();
  }

  private checkAuthorDiscount(): void {
    this.kitPaymentService.getAuthorDiscount(this.book.idKit)
      .subscribe({
        next: (response) => {
          if (!response.isError && response.isAuthor) {
            this.isAuthor = true;
            this.authorMaxDiscountPerUnit = response.maxDiscountValue;
            this.emitAuthorDiscount();
          }
        },
        error: () => {
          // Silently fail — author discount is a bonus, not critical
        }
      });
  }

  private formatBRL(value: number): string {
    return value.toFixed(2).replace('.', ',');
  }

  private emitAuthorDiscount(): void {
    const totalDiscount = this.authorMaxDiscountPerUnit * this.book.quantity;
    this.discountMessage = `Você é o autor! Desconto de R$${this.formatBRL(totalDiscount)} aplicado (${this.book.quantity}x R$${this.formatBRL(this.authorMaxDiscountPerUnit)}).`;
    this.applyDiscount.emit({
      idKit: this.book.idKit,
      discountCode: '__AUTHOR_DISCOUNT__',
      discountValue: totalDiscount
    });
  }

  increaseQuantity(): void {
    this.book.quantity++;
    this.emitQuantityChange();
  }

  decreaseQuantity(): void {
    if (this.book.quantity > 1) {
      this.book.quantity--;
      this.emitQuantityChange();
    }
  }

  emitQuantityChange(): void {
    this.quantityChange.emit({ idKit: this.book.idKit, quantity: this.book.quantity });
    // Re-calcula desconto do autor considerando a nova quantidade
    if (this.isAuthor) {
      this.emitAuthorDiscount();
    }
  }

  // Simula a validação do código de desconto do autor
  validateAndApplyDiscount(): void {
    if (this.isAuthor) return;

    if (!this.authorDiscountCode) {
      this.discountMessage = '';
      this.applyDiscount.emit({ idKit: this.book.idKit, discountCode: this.authorDiscountCode, discountValue: 0 });
      return;
    }

    this.kitPaymentService.getCoupon(this.authorDiscountCode, this.book.idKit)
      .subscribe((response: GetKitPaymentCouponResponse) => {
        if (response.isError) {
          this.alertService.show('Erro', response.errorDescription || 'Não foi possível validar o cupom. Tente novamente.', AlertType.error);
          return;
        }

        if (response.isValid) {
          let discountPerUnit: number;

          if (response.discountPercentage) {
            discountPerUnit = this.book.totalPrice * (response.discountPercentage / 100);
          } else {
            discountPerUnit = response.discountValue;
          }

          // Garante que o preço final não fique abaixo do valor mínimo de venda (evita prejuízo)
          const minSalePrice = response.minSalePrice ?? 0;
          if (minSalePrice > 0) {
            const maxDiscountPerUnit = this.book.totalPrice - minSalePrice;
            if (discountPerUnit > maxDiscountPerUnit) {
              discountPerUnit = Math.max(0, maxDiscountPerUnit);
              this.discountMessage = `Desconto ajustado para o valor mínimo de venda: R$${this.formatBRL(minSalePrice * this.book.quantity)} (${this.book.quantity}x R$${this.formatBRL(minSalePrice)})`;
            } else {
              this.discountMessage = response.discountPercentage
                ? `Desconto de ${response.discountPercentage}% aplicado!`
                : `Desconto de R$${this.formatBRL(discountPerUnit * this.book.quantity)} aplicado!`;
            }
          } else {
            this.discountMessage = response.discountPercentage
              ? `Desconto de ${response.discountPercentage}% aplicado!`
              : `Desconto de R$${this.formatBRL(discountPerUnit * this.book.quantity)} aplicado!`;
          }

          const totalDiscount = discountPerUnit * this.book.quantity;
          this.applyDiscount.emit({ idKit: this.book.idKit, discountCode: this.authorDiscountCode, discountValue: totalDiscount });

        } else {
          this.discountMessage = 'Código inválido para este autor';
          this.applyDiscount.emit({ idKit: this.book.idKit, discountCode: this.authorDiscountCode, discountValue: 0 });
        }

      }, error => {
        console.error('Erro na requisição de pagamento', error);
        alert('Ocorreu um erro inesperado. Tente novamente.');
      });
  }
}